import { ReactNode, createContext, useContext, useMemo } from 'react';
import { useQuery } from 'react-query';

import getInvitations from 'api/matchmaking/getInvitations';

import { IUseControllerContextProps } from './types';

const useControllerContext = createContext<IUseControllerContextProps>(
  {} as IUseControllerContextProps,
);

interface IUseControllerProviderProps {
  children: ReactNode;
}

const UseControllerInvitationsProvider: React.FC<IUseControllerProviderProps> = ({ children }) => {
  const { data: invitationsData = [], isLoading: isInvitationsLoading } = useQuery(
    ['getInvitations'],
    () => getInvitations(),
  );

  const invitationsSent = useMemo(
    () => invitationsData?.filter((invitation) => invitation.owner) || [],
    [invitationsData],
  );

  const invitationsReceived = useMemo(
    () => invitationsData?.filter((invitation) => !invitation.owner) || [],
    [invitationsData],
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const contextValue = useMemo(
    () => ({ isLoading: isInvitationsLoading, invitationsSent, invitationsReceived }),
    [isInvitationsLoading, invitationsSent, invitationsReceived],
  );

  return (
    <useControllerContext.Provider value={contextValue}>{children}</useControllerContext.Provider>
  );
};

const useControllerInvitations = (): IUseControllerContextProps => {
  const context = useContext(useControllerContext);
  if (!context)
    throw new Error(
      'useControllerInvitations must be used within an UseControllerInvitationsProvider',
    );
  return context;
};

export { UseControllerInvitationsProvider, useControllerInvitations };
